import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { Skeleton } from 'primereact/skeleton';
import API_BASE_URL from '../config/api';
import '../styles/TierSelection.css';

const SCHOOL_NAME = 'UniStreaming';
const STANDARD_TIER_ID = 1;
const SKIP_LINE_TIER_ID = 2;

const StreamerTierSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);

  const { submissionId, submissionData } = location.state || {};

  const [tiers, setTiers] = useState([]);
  const [selectedTierId, setSelectedTierId] = useState(SKIP_LINE_TIER_ID);
  const [instantOnlyPricing, setInstantOnlyPricing] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/streamer/pricing`);
        const result = await response.json();

        if (result.status === 'success') {
          const list = result.data.tiers || [];
          setTiers(list);
          setInstantOnlyPricing(!!result.data.instantOnlyPricing);
          const skip = list.find((t) => t.isSkipLine) || list.find((t) => t.id === SKIP_LINE_TIER_ID);
          const standard = list.find((t) => t.id === STANDARD_TIER_ID) || list.find((t) => !t.isSkipLine);
          setSelectedTierId(skip?.id || standard?.id || SKIP_LINE_TIER_ID);
        } else {
          throw new Error(result.message || 'Failed to load posting options');
        }
      } catch (error) {
        console.error('Error fetching posting options:', error);
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load posting options. Please try again.',
          life: 5000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  useEffect(() => {
    const verifySubmission = async () => {
      if (!submissionId || !submissionData) {
        navigate('/streamer/submit');
        return;
      }

      const photos = location.state?.photos;
      if (photos && photos.length > 0) return;

      try {
        const response = await fetch(`${API_BASE_URL}/api/streamer/submissions/${submissionId}/verify`);
        const result = await response.json();

        if (result.status === 'error' || !result.data?.hasMedia) {
          toastRef.current?.show({
            severity: 'warn',
            summary: 'Media Required',
            detail: 'Please upload media before submitting.',
            life: 3000
          });
          setTimeout(() => {
            navigate('/streamer/media', { state: { submissionData, submissionId } });
          }, 1000);
        }
      } catch (error) {
        console.error('Error verifying submission:', error);
        toastRef.current?.show({
          severity: 'warn',
          summary: 'Media Required',
          detail: 'Please upload media before submitting.',
          life: 3000
        });
        setTimeout(() => {
          navigate('/streamer/media', { state: { submissionData, submissionId } });
        }, 1000);
      }
    };

    verifySubmission();
  }, [submissionId, submissionData, navigate, location.state]);

  const selectedTier = tiers.find((t) => t.id === selectedTierId);
  const isSkipLine = !!selectedTier?.isSkipLine || selectedTierId === SKIP_LINE_TIER_ID;
  const priceDisplay =
    selectedTier?.finalPriceDisplay ||
    (selectedTier?.finalPrice != null
      ? `$${Math.floor(selectedTier.finalPrice / 100)}`
      : '$5');

  const handleContinue = async () => {
    if (isProcessing || !selectedTierId) return;

    setIsProcessing(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/streamer/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionId, tierId: selectedTierId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit post');
      }

      const result = await response.json();

      if (result.isFree || result.redirectUrl) {
        navigate(`/streamer/thank-you/${submissionId}`);
        return;
      }

      if (result.url) {
        window.location.href = result.url;
        return;
      }

      throw new Error('Unexpected response from server');
    } catch (error) {
      console.error('Submit error:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to submit your post. Please try again.',
        life: 5000
      });
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    navigate('/streamer/media', {
      state: { submissionData, submissionId, photos: location.state?.photos }
    });
  };

  if (!submissionId || !submissionData) {
    return null;
  }

  return (
    <div className="tier-selection-page">
      <Toast ref={toastRef} />

      <header className="form-header">
        <div className="header-content">
          <Button
            icon="pi pi-arrow-left"
            label="Back"
            className="back-button"
            onClick={handleBack}
            disabled={isProcessing}
          />
          <div className="header-title">
            <p>{SCHOOL_NAME}</p>
          </div>
        </div>
      </header>

      <div className="progress-section">
        <div className="container">
          <div className="progress-info">
            <span>{instantOnlyPricing ? 'Final Step: Confirm Instant Post' : 'Final Step: Choose How to Post'}</span>
          </div>
          <ProgressBar value={90} className="form-progress" showValue={false} />
        </div>
      </div>

      <main className="tier-selection-main">
        <div className="container">
          {instantOnlyPricing && (
            <div className="seeding-mode-notice">
              <i className="pi pi-info-circle"></i>
              <span>Your post will go live instantly for $5.</span>
            </div>
          )}

          <Card className="tier-selection-card">
            <h2 className="tier-title">
              {instantOnlyPricing ? 'Instant Posting' : 'Choose Your Option'}
            </h2>
            <p className="tier-subtitle">
              {instantOnlyPricing
                ? 'Confirm your $5 Instant post to go live right away'
                : 'Free posts join the schedule. Skip the Line puts you next — still one post every 20 minutes.'}
            </p>

            {loading ? (
              <div className="tier-skeleton">
                <div className="skeleton-tier">
                  <Skeleton width="100%" height="120px" borderRadius="12px" />
                </div>
                {!instantOnlyPricing && (
                  <div className="skeleton-tier">
                    <Skeleton width="100%" height="120px" borderRadius="12px" />
                  </div>
                )}
              </div>
            ) : (
              <div className={`tier-list ${instantOnlyPricing ? 'tier-list-single' : ''}`}>
                {tiers.map((tier) => {
                  const selected = selectedTierId === tier.id;
                  const priceLabel =
                    tier.finalPriceDisplay ||
                    (tier.finalPrice === 0 ? 'Free' : `$${Math.floor(tier.finalPrice / 100)}`);
                  return (
                    <div
                      key={tier.id}
                      className={`tier-option ${selected ? 'selected' : ''} ${tier.isSkipLine ? 'tier-skip-line' : ''}`}
                      onClick={() => setSelectedTierId(tier.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setSelectedTierId(tier.id);
                      }}
                    >
                      <div className="tier-header">
                        <div className="tier-name-section">
                          <h3 className="tier-name">{tier.name}</h3>
                          {(tier.isSkipLine || instantOnlyPricing) && (
                            <span className="tier-badge badge-popular">
                              {instantOnlyPricing ? 'Instant' : 'Priority'}
                            </span>
                          )}
                        </div>
                        <div className={`tier-price ${tier.finalPrice === 0 ? 'tier-price-free' : ''}`}>
                          {priceLabel}
                        </div>
                      </div>
                      <div className="tier-description">
                        {instantOnlyPricing
                          ? 'Post immediately — highest priority'
                          : tier.description}
                      </div>
                      {selected && (
                        <div className="tier-selected-indicator">
                          <i className="pi pi-check-circle"></i>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="tier-actions">
              <Button
                label={
                  isProcessing
                    ? isSkipLine || instantOnlyPricing
                      ? 'Going to Payment...'
                      : 'Submitting...'
                    : instantOnlyPricing || isSkipLine
                      ? `Continue to Payment — ${priceDisplay}`
                      : 'Submit Post (Free)'
                }
                icon={isProcessing ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-right'}
                iconPos="right"
                onClick={handleContinue}
                disabled={isProcessing || loading || !selectedTierId}
                className="tier-continue-button"
              />
              <p className="tier-note">
                {instantOnlyPricing
                  ? "You'll be redirected to secure payment. Your post will be published instantly."
                  : isSkipLine
                    ? 'You’ll skip ahead of free posts. If others already chose Skip the Line, you’ll be next after them. Posts still go out every 20 minutes.'
                    : 'Your post joins the free schedule and goes live in order when a slot opens (every 20 minutes during posting hours).'}
              </p>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default StreamerTierSelectionPage;
